import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { revenueAPI, commissionAPI } from '../services/api';

const PAYOUT_COLORS = { pending: '#f59e0b', approved: '#3b82f6', paid: '#22c55e', on_hold: '#ef4444' };
const COMM_TYPE_LABELS = { personal: 'Personal', override: 'Override', team_bonus: 'Team Bonus', quarterly_bonus: 'Quarterly Bonus', salary: 'Salary', stipend: 'Stipend' };

function ProgressBar({ value, max, color = '#6366f1', label }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
        <span style={{ fontWeight: 600, color: '#374151' }}>{label}</span>
        <span style={{ color: '#64748b' }}>{value} / {max} ({pct}%)</span>
      </div>
      <div style={{ height: 10, background: '#e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 8, transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

function TierMarker({ label, threshold, achieved, color }) {
  const achieved_pct = Math.min(100, achieved);
  const passed = achieved >= threshold;
  return (
    <div style={{ position: 'absolute', left: `${threshold}%`, top: -4, transform: 'translateX(-50%)', textAlign: 'center' }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: passed ? color : '#d1d5db', border: `2px solid ${passed ? color : '#94a3b8'}`, margin: '0 auto' }} />
      <div style={{ fontSize: 10, color: passed ? color : '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function MyEarningsPage() {
  const [earnings, setEarnings] = useState(null);
  const [entries, setEntries] = useState([]);
  const [paidEntries, setPaidEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const [eRes, allRes, paidRes] = await Promise.all([
        revenueAPI.getMyEarnings(),
        commissionAPI.getEntries({ month: now.getMonth() + 1, year: now.getFullYear() }),
        commissionAPI.getEntries({ payout_status: 'paid' }),
      ]);
      setEarnings(eRes.data.data);
      setEntries(allRes.data.data || []);
      setPaidEntries(paidRes.data.data || []);
    } catch {
      showToast('Failed to load earnings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>;

  const e = earnings || {};
  const target = e.target || {};
  const monthEntries = e.entries || [];
  const chartData = e.chart_data || [];

  const totalEarnings = monthEntries.reduce((s, en) => s + (en.commission_amount || 0), 0);
  const personalComm = monthEntries.filter(en => en.commission_type === 'personal').reduce((s, en) => s + en.commission_amount, 0);
  const overrideComm = monthEntries.filter(en => en.commission_type === 'override').reduce((s, en) => s + en.commission_amount, 0);
  const bonuses = monthEntries.filter(en => ['team_bonus', 'quarterly_bonus'].includes(en.commission_type)).reduce((s, en) => s + en.commission_amount, 0);
  const salary = monthEntries.filter(en => ['salary', 'stipend'].includes(en.commission_type)).reduce((s, en) => s + en.commission_amount, 0);

  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - now.getDate();

  // Quarterly progress
  const getQuarter = m => Math.ceil(m / 3);
  const currentQ = getQuarter(now.getMonth() + 1);
  const qTarget = (target.target_revenue || 0) * 3;
  const qActual = (target.actual_revenue || 0);
  const qAchievement = qTarget > 0 ? Math.min(150, Math.round((qActual / qTarget) * 100)) : 0;

  return (
    <div style={{ padding: '24px 32px', maxWidth: 960, margin: '0 auto' }}>
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'error' ? '#fee2e2' : '#dcfce7',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          borderRadius: 8, padding: '12px 20px', fontWeight: 600, fontSize: 14,
        }}>{toast.msg}</div>
      )}

      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>My Earnings</h1>
      <p style={{ color: '#64748b', marginBottom: 28, marginTop: 0 }}>
        {new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' })}
      </p>

      {/* This Month Card */}
      <div style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', borderRadius: 16, padding: '28px 32px', color: '#fff', marginBottom: 24 }}>
        <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 8 }}>Total Earnings This Month</div>
        <div style={{ fontSize: 42, fontWeight: 800 }}>₹{totalEarnings.toLocaleString('en-IN')}</div>
        <div style={{ display: 'flex', gap: 24, marginTop: 20, flexWrap: 'wrap' }}>
          {[
            { label: 'Personal Commission', value: personalComm },
            { label: 'Override Commission', value: overrideComm },
            { label: 'Bonuses', value: bonuses },
            { label: 'Salary / Stipend', value: salary },
          ].map(b => (
            <div key={b.label}>
              <div style={{ fontSize: 11, opacity: 0.75 }}>{b.label}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>₹{b.value.toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Target Progress */}
      {target.user_id && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', margin: 0 }}>Target Progress</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: target.salary_released ? '#dcfce7' : target.achievement_percentage >= (target.target_revenue > 0 ? 50 : 0) ? '#fef9c3' : '#fee2e2',
                color: target.salary_released ? '#16a34a' : '#b45309',
              }}>
                Salary: {target.salary_released ? 'Released' : `Accruing ${target.achievement_percentage || 0}%`}
              </div>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>{daysRemaining} days remaining</span>
            </div>
          </div>
          <ProgressBar value={target.actual_enrollments || 0} max={target.target_enrollments || 1} color="#6366f1" label="Enrollments" />
          <ProgressBar value={target.actual_mous || 0} max={target.target_mous || 1} color="#22c55e" label="MoUs" />
          <ProgressBar value={target.actual_revenue || 0} max={target.target_revenue || 1} color="#f59e0b" label={`Revenue — ₹${(target.actual_revenue || 0).toLocaleString('en-IN')} / ₹${(target.target_revenue || 0).toLocaleString('en-IN')}`} />
        </div>
      )}

      {/* Earnings Chart */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Earnings Last 12 Months</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={v => `₹${v / 1000}K`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={v => `₹${v?.toLocaleString('en-IN')}`} />
            <Bar dataKey="total" name="Total Earnings" fill="#6366f1" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Quarterly Bonus Progress */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Q{currentQ} Quarterly Bonus Progress</h3>
        <div style={{ position: 'relative', margin: '24px 0 48px' }}>
          <div style={{ height: 12, background: '#e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, qAchievement)}%`, background: qAchievement >= 100 ? '#22c55e' : '#6366f1', borderRadius: 8, transition: 'width 0.4s' }} />
          </div>
          {[{ label: 'Tier 1', t: 80, color: '#f59e0b' }, { label: 'Tier 2', t: 100, color: '#6366f1' }, { label: 'Tier 3', t: 120, color: '#22c55e' }].map(m => (
            <TierMarker key={m.t} label={`${m.label} ${m.t}%`} threshold={Math.min(100, m.t)} achieved={qAchievement} color={m.color} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>{qAchievement}%</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Achievement</div>
          </div>
          <div style={{ flex: 1, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1e293b' }}>₹{(qActual || 0).toLocaleString('en-IN')}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Revenue vs ₹{(qTarget || 0).toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>

      {/* Commission Breakdown Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Commission Breakdown — This Month</h3>
        {entries.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 20 }}>No entries for this month yet.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Type', 'Base Amount', 'Rate', 'Commission', 'Status'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((en, i) => {
                const c = PAYOUT_COLORS[en.payout_status] || '#94a3b8';
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#374151' }}>{COMM_TYPE_LABELS[en.commission_type] || en.commission_type}</td>
                    <td style={{ padding: '10px 12px' }}>₹{(en.base_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 12px', color: '#64748b' }}>{en.rate || 0}%</td>
                    <td style={{ padding: '10px 12px', fontWeight: 700, color: '#6366f1' }}>₹{(en.commission_amount || 0).toLocaleString('en-IN')}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: `${c}20`, color: c }}>{en.payout_status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Payout History */}
      {paidEntries.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 16, marginTop: 0 }}>Payout History</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Month/Year', 'Type', 'Amount', 'Paid On', 'Reference'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#374151', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paidEntries.slice(0, 20).map((en, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', color: '#374151' }}>{en.month}/{en.year}</td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{COMM_TYPE_LABELS[en.commission_type] || en.commission_type}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: '#22c55e' }}>₹{(en.commission_amount || 0).toLocaleString('en-IN')}</td>
                  <td style={{ padding: '10px 12px', color: '#64748b', fontSize: 12 }}>{en.paid_at ? new Date(en.paid_at).toLocaleDateString('en-IN') : '—'}</td>
                  <td style={{ padding: '10px 12px', color: '#374151' }}>{en.payment_reference || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
